import React, { useState, useEffect } from 'react';
import { getTestimonials, deleteTestimonial, syncGoogleReviews } from '../../services/testimonialsService';
import Loader from '../../components/common/Loader';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircleIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline';

// Helper to extract YouTube video ID from various URL formats
function getYouTubeVideoId(url) {
  if (!url) return null;
  const regExp = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([\w-]{11})(?:\S+)?/;
  const match = url.match(regExp);
  return (match && match[1].length === 11) ? match[1] : null;
}

// Helper to extract Facebook video thumbnail (placeholder for now)
function getFacebookVideoThumbnail(url) {
  if (!url) return null;
  // Facebook doesn't provide easy thumbnail access like YouTube
  // Return a placeholder or use Facebook Graph API in production
  return 'https://via.placeholder.com/120x68/4267B2/ffffff?text=FB';
}

function TestimonialListPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTestimonials();
      console.log('Fetched testimonials:', data); // Debug log
      setTestimonials(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load testimonials: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      setLoading(true);
      setError('');
      try {
        await deleteTestimonial(id);
        await fetchTestimonials();
        alert('Testimonial deleted successfully!');
      } catch (err) {
        setError('Failed to delete testimonial: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSyncGoogleReviews = async () => {
    if (window.confirm('Are you sure you want to sync Google Reviews? This may take a few moments.')) {
      setLoading(true);
      setError('');
      try {
        const result = await syncGoogleReviews(20);
        console.log('Sync result:', result);
        alert('Google Reviews sync started successfully!');
        await fetchTestimonials(); // Refresh the list
      } catch (err) {
        console.error('Sync error:', err);
        setError('Failed to sync Google reviews: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const headers = ['Source', 'Name / Title', 'Rating', 'Details', 'Featured', 'Active', 'Actions'];

  const renderRow = (testimonial) => {
    const isYouTubePrimary = testimonial.source_type === 'youtube';
    const isFacebookPrimary = testimonial.source_type === 'facebook';
    const hasVideoUrl = !!testimonial.video_url;
    const youtubeId = hasVideoUrl && testimonial.video_url.includes('youtube') ? getYouTubeVideoId(testimonial.video_url) : null;
    const youtubeThumbnail = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/default.jpg` : null;
    const facebookThumbnail = hasVideoUrl && testimonial.video_url.includes('facebook') ? getFacebookVideoThumbnail(testimonial.video_url) : null;

    return (
      <tr key={testimonial.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-teal-50 transition-all duration-200">
        <td className="px-6 py-4 text-sm font-medium text-gray-900">
          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
            testimonial.source_type === 'google_reviews' ? 'bg-red-100 text-red-800 border border-red-200' :
            testimonial.source_type === 'youtube' ? 'bg-red-100 text-red-800 border border-red-200' :
            testimonial.source_type === 'facebook' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
            'bg-gray-100 text-gray-800 border border-gray-200'
          }`}>
            {testimonial.source_type?.replace('_', ' ')}
          </span>
        </td>
        <td className="px-6 py-4 text-sm text-gray-900 font-medium">
          {isYouTubePrimary ? (
            <span className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
              YouTube Video
            </span>
          ) : isFacebookPrimary ? (
            <span className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Facebook Video
            </span>
          ) : (
            testimonial.customer_name
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
          {testimonial.rating ? (
            <div className="flex items-center">
              <div className="flex text-yellow-400 text-sm mr-2">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}>
                    ★
                  </span>
                ))}
              </div>
              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-lg text-xs font-medium">
                {testimonial.rating}/5
              </span>
            </div>
          ) : (
            <span className="text-gray-400 text-xs">No Rating</span>
          )}
        </td>
        <td className="px-6 py-4 text-sm text-gray-500">
          {hasVideoUrl && testimonial.video_url.includes('youtube') ? (
            <div className="flex items-center space-x-3">
              {youtubeThumbnail && (
                <img src={youtubeThumbnail} alt="YouTube Thumbnail" className="h-12 w-20 object-cover rounded-lg shadow-sm border border-gray-200" />
              )}
              <a 
                href={testimonial.video_url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded-lg transition-all duration-200 text-xs font-medium"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                Watch Video
              </a>
            </div>
          ) : hasVideoUrl && testimonial.video_url.includes('facebook') ? (
            <div className="flex items-center space-x-3">
              {facebookThumbnail && (
                <img src={facebookThumbnail} alt="Facebook Thumbnail" className="h-12 w-20 object-cover rounded-lg shadow-sm border border-gray-200" />
              )}
              <a 
                href={testimonial.video_url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1 rounded-lg transition-all duration-200 text-xs font-medium"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Watch Video
              </a>
            </div>
          ) : (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-xs text-gray-600 max-w-xs">
              {testimonial.review_text?.substring(0, 100) + (testimonial.review_text && testimonial.review_text.length > 100 ? '...' : '')}
            </div>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
          {testimonial.is_featured ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
              Featured
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
              <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
              Regular
            </span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
          {testimonial.is_active ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border border-red-200">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
              Inactive
            </span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
          {testimonial.source_type !== 'google_reviews' && (
            <Link 
              to={`/testimonials/edit/${testimonial.id}`} 
              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1 rounded-lg transition-all duration-200"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </Link>
          )}
          <button 
            onClick={() => handleDelete(testimonial.id)} 
            className="inline-flex items-center text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1 rounded-lg transition-all duration-200"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </td>
      </tr>
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative m-6 shadow-md" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center px-2">
          <div className="w-3 h-3 bg-teal-500 rounded-full mr-3"></div>
          Testimonials Management
        </h2>

      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl shadow-lg p-6 mb-6 border border-teal-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <div className="w-3 h-3 bg-teal-500 rounded-full mr-3"></div>
              All Testimonials
            </h3>
            <p className="text-sm text-gray-600 mt-2 bg-white px-3 py-1 rounded-lg border border-teal-200">
              Manage customer testimonials, reviews, and video content
            </p>
          </div>
          <div className="flex space-x-3">
            <Button 
              variant="secondary" 
              onClick={handleSyncGoogleReviews} 
              className="inline-flex items-center py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
            >
              <CloudArrowDownIcon className="h-5 w-5 mr-2" /> Sync Google Reviews
            </Button>
            <Link to="/testimonials/new">
              <Button 
                variant="primary" 
                className="inline-flex items-center py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
              >
                <PlusCircleIcon className="h-5 w-5 mr-2" /> Add New Testimonial
              </Button>
            </Link>
          </div>
        </div>

        <Table headers={headers} data={testimonials} renderRow={renderRow} />
      </div>
      </div>
    </div>
  );
}

export default TestimonialListPage;